import React, { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { EmptyState, InlineLoader } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { UserCog, ShieldCheck, Shield, Plus, Trash2, Loader2, Crown } from "lucide-react";
import { toast } from "sonner";

export default function AdminManagement() {
  const { user } = useAuth();
  const [admins, setAdmins] = useState(null);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "" });

  const load = () => api.get("/admin/admins").then((r) => setAdmins(r.data)).catch(() => setAdmins([]));
  useEffect(() => { load(); }, []);

  const createAdmin = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.post("/admin/admins", form);
      toast.success(`Admin "${form.name}" created.`);
      setForm({ name: "", email: "", password: "" });
      setOpen(false);
      load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setCreating(false); }
  };

  const changeRole = async (a, role) => {
    setBusy(a.id);
    try {
      await api.patch(`/admin/admins/${a.id}/role`, { role });
      toast.success(`${a.name} is now ${role === "superadmin" ? "a SuperAdmin" : "an Admin"}.`);
      load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(""); }
  };

  const removeAdmin = async (a) => {
    setBusy(a.id);
    try {
      await api.delete(`/admin/admins/${a.id}`);
      toast.success(`${a.name} removed.`);
      load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(""); }
  };

  return (
    <div data-testid="admin-management-page">
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight text-ff-blue sm:text-3xl">Admins</h1>
          <p className="mt-1 text-sm text-ff-medium">Manage who can access the FightFund admin console. Only SuperAdmins can add or remove admins.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-ff-red text-white hover:bg-ff-red/90" data-testid="add-admin-button">
              <Plus className="mr-1.5 h-4 w-4" /> Add Admin
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="add-admin-dialog">
            <DialogHeader><DialogTitle>Add a new admin</DialogTitle></DialogHeader>
            <form onSubmit={createAdmin} className="space-y-4" id="add-admin-form">
              <div>
                <Label htmlFor="a-name">Full name</Label>
                <Input id="a-name" required data-testid="admin-name-input" value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" placeholder="Jane Admin" />
              </div>
              <div>
                <Label htmlFor="a-email">Email</Label>
                <Input id="a-email" type="email" required data-testid="admin-email-input" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" placeholder="admin@fightfund.org" />
              </div>
              <div>
                <Label htmlFor="a-pass">Temporary password</Label>
                <Input id="a-pass" type="text" required minLength={8} data-testid="admin-password-input" value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1.5" placeholder="At least 8 characters" />
                <p className="mt-1 text-xs text-ff-medium">Share this securely; they can reset it later.</p>
              </div>
            </form>
            <DialogFooter>
              <Button type="submit" form="add-admin-form" disabled={creating} data-testid="submit-admin-button"
                className="bg-ff-blue text-white hover:bg-ff-blue/90">
                {creating ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Creating</> : "Create admin"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {admins === null ? (
        <InlineLoader label="Loading admins" />
      ) : admins.length === 0 ? (
        <EmptyState icon={UserCog} title="No admins yet" message="Add your first admin to get started." testid="admins-empty" />
      ) : (
        <div className="overflow-hidden rounded-xl border border-ff-border bg-white shadow-sm" data-testid="admins-table">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#F3F4F6]">
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {admins.map((a) => {
                const isSelf = a.id === user?.id;
                const isSuper = a.role === "superadmin";
                return (
                  <TableRow key={a.id} data-testid={`admin-row-${a.id}`}>
                    <TableCell className="font-heading font-bold text-ff-blue">
                      {a.name} {isSelf && <span className="ml-1 text-xs font-semibold text-ff-medium">(you)</span>}
                    </TableCell>
                    <TableCell className="text-ff-dark">{a.email}</TableCell>
                    <TableCell>
                      <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold ${isSuper ? "bg-[#FEF2F2] text-ff-red" : "bg-[#EFF6FF] text-[#1D4ED8]"}`}>
                        {isSuper ? <Crown className="h-3 w-3" /> : <Shield className="h-3 w-3" />}
                        {isSuper ? "SuperAdmin" : "Admin"}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        {isSuper ? (
                          <Button size="sm" variant="outline" disabled={busy === a.id || isSelf}
                            data-testid={`demote-${a.id}`} onClick={() => changeRole(a, "admin")}
                            className="border-ff-border text-ff-dark">
                            Make Admin
                          </Button>
                        ) : (
                          <Button size="sm" variant="outline" disabled={busy === a.id}
                            data-testid={`promote-${a.id}`} onClick={() => changeRole(a, "superadmin")}
                            className="border-ff-red text-ff-red hover:bg-ff-red hover:text-white">
                            <ShieldCheck className="mr-1 h-3.5 w-3.5" /> Make SuperAdmin
                          </Button>
                        )}
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button size="sm" variant="ghost" disabled={busy === a.id || isSelf}
                              data-testid={`remove-${a.id}`} className="text-ff-red hover:bg-[#FEF2F2]">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Remove {a.name}?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This permanently removes {a.email}'s admin access. This cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction data-testid={`confirm-remove-${a.id}`} onClick={() => removeAdmin(a)}
                                className="bg-ff-red text-white hover:bg-ff-red/90">Remove admin</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
